/**
 * Display names for transcription backends and LLM models.
 *
 * The backend returns raw provider/model ids from the usage ledger
 * (e.g. "deepgram-streaming", "claude-opus-4-8"). These are product proper
 * nouns — not translatable — so we map them to compact, human labels here, with
 * a graceful fallback so an unknown id still renders sanely instead of leaking a
 * raw slug. Kept frontend-side because the card is space-constrained and wants
 * short forms ("Opus 4.8"), not the full catalog name.
 */

const BACKEND_NAMES: Record<string, string> = {
  deepgram: "Deepgram",
  soniox: "Soniox",
  assemblyai: "AssemblyAI",
  azure: "Azure Speech",
  mai: "MAI-Transcribe-2",
  "azure-llm": "Azure LLM-speech",
  speechmatics: "Speechmatics",
  openai: "Whisper",
  local: "Local",
}

const MODEL_NAMES: Record<string, string> = {
  "claude-fable-5-1": "Fable 5.1",
  "claude-opus-5": "Opus 5",
  "claude-sonnet-5": "Sonnet 5",
  "claude-opus-4-8": "Opus 4.8",
  "claude-opus-4-6": "Opus 4.6",
  "claude-sonnet-4-6": "Sonnet 4.6",
  "claude-haiku-4-5-20251001": "Haiku 4.5",
  "gpt-4o": "GPT-4o",
  "gpt-4o-mini": "GPT-4o mini",
  "gpt-4-turbo": "GPT-4 Turbo",
}

const titleCase = (s: string): string => s.charAt(0).toUpperCase() + s.slice(1)

/** Drop a trailing date stamp ("-20251001" or "-2024-08-06") used to pin model snapshots. */
const stripDateSuffix = (id: string): string => id.replace(/-\d{8}$/, "").replace(/-\d{4}-\d{2}-\d{2}$/, "")

/**
 * Pretty label for a transcription backend id. Streaming/batch variants share a
 * name ("deepgram-streaming" → "Deepgram"). Returns undefined for empty input.
 */
export function formatTranscriptionBackend(raw: string | null | undefined): string | undefined {
  if (!raw) return undefined
  const base = raw.toLowerCase().replace(/-(streaming|batch)$/, "")
  return BACKEND_NAMES[base] ?? titleCase(base)
}

/**
 * Compact label for an LLM model id ("claude-opus-4-8" → "Opus 4.8"). Falls back
 * to a cleaned form: Claude ids drop the vendor prefix and join the numeric
 * version with dots; others strip any date stamp. Returns undefined for empty input.
 */
export function formatModelName(raw: string | null | undefined): string | undefined {
  if (!raw) return undefined
  // OpenRouter ids carry a vendor namespace ("anthropic/claude-sonnet-4-6");
  // strip it so they normalise to the same label as the direct-API id
  // ("claude-sonnet-4-6") instead of falling through to the generic fallback.
  const key = (raw.toLowerCase().split("/").pop() ?? "").trim()
  if (!key) return undefined
  if (MODEL_NAMES[key]) return MODEL_NAMES[key]

  const cleaned = stripDateSuffix(key)
  if (cleaned.startsWith("claude-")) {
    const [family, ...rest] = cleaned.slice("claude-".length).split("-")
    const version = rest.filter((p) => /^\d{1,2}$/.test(p)).join(".")
    return version ? `${titleCase(family)} ${version}` : titleCase(family)
  }
  if (cleaned.startsWith("gpt")) return `GPT-${cleaned.replace(/^gpt-?/, "")}`
  return cleaned.split(/[-_]/).map(titleCase).join(" ")
}
